import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { useDemoMode } from '@/contexts/demo/DemoContext';
import type { SiteTimeline } from '@/domain/fishGroups';
import { getDemoDisplayName } from '@/utils/demoMasking';

import { maskTimeline } from '../helpers/demoMask';

/**
 * Display names for the board while demo mode is on; the real name otherwise. These are for
 * labels only: anything that computes with a name (suggestions, duplicate checks, requests)
 * must use the real one.
 */
export interface BoardDemoMask {
    /**
     * Creating is off in demo mode: a demo must not write real data, and a create form cannot
     * both save real names and show only masked ones.
     */
    writesDisabled: boolean;
    locationName: (locationId: number, name: string) => string;
    penName: (cageId: number, name: string) => string;
    cycleName: (cycleId: number, name: string) => string;
    /** The board rows only render, so the whole timeline can be masked for them. */
    timeline: (timeline: SiteTimeline | null) => SiteTimeline | null;
}

export function useBoardDemoMask(): BoardDemoMask {
    const { isDemoMode } = useDemoMode();
    const { t } = useTranslation();

    return useMemo(
        () => ({
            writesDisabled: isDemoMode,
            locationName: (id, name) => (isDemoMode ? getDemoDisplayName(id, 'location', t) : name),
            penName: (id, name) => (isDemoMode ? getDemoDisplayName(id, 'cage', t) : name),
            cycleName: (id, name) =>
                isDemoMode ? getDemoDisplayName(id, 'stockingCycle', t) : name,
            timeline: (timeline) => (isDemoMode && timeline ? maskTimeline(timeline, t) : timeline),
        }),
        [isDemoMode, t]
    );
}
